import React from "react";
import { Input, InputNumber, Select, Space } from "antd";
import {
    MemberMetadataField,
    MemberMetadataTypeDescriptor,
} from "../../../../model/members/MemberMetadataTypeDescriptor";
import { tokens } from "../../../../core/theme/tokens";

// The in-editor representation of a metadata value, per value kind:
//   text/enum -> string, number -> number|null, list -> string[], json -> object.
// The card and modal keep this shape while editing and serialise it back to the
// stored string form (comma-list / JSON) only on save, so the backend contract
// stays plain-text.
export type EditorValue = string | number | null | string[] | Record<string, string>;

// Parse the stored member_metadata string into the editor representation.
export const parseStoredValue = (
    descriptor: MemberMetadataTypeDescriptor,
    stored: string | null | undefined,
): EditorValue => {
    switch (descriptor.valueKind) {
        case "number":
            return stored === undefined || stored === null || stored === "" ? null : Number(stored);
        case "list":
            return stored ? stored.split(",").map((item) => item.trim()).filter(Boolean) : [];
        case "json":
            try {
                return stored ? (JSON.parse(stored) as Record<string, string>) : {};
            } catch {
                return {};
            }
        case "text":
        case "enum":
        default:
            return stored ?? "";
    }
};

// Serialise the editor representation back to the stored string form.
export const serializeValue = (descriptor: MemberMetadataTypeDescriptor, value: EditorValue): string => {
    switch (descriptor.valueKind) {
        case "number":
            return value === null || value === undefined || Number.isNaN(value) ? "" : String(value);
        case "list":
            return Array.isArray(value) ? value.join(",") : "";
        case "json": {
            const object = (value ?? {}) as Record<string, string>;
            const trimmed: Record<string, string> = {};
            (descriptor.fields ?? []).forEach((field) => {
                const fieldValue = object[field.name];
                if (fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== "")
                    trimmed[field.name] = String(fieldValue).trim();
            });
            return JSON.stringify(trimmed);
        }
        case "text":
        case "enum":
        default:
            return String(value ?? "").trim();
    }
};

// Whether the current editor value is complete enough to save (mirrors the
// server-side rules so we don't submit a value the API will reject).
export const isValidValue = (descriptor: MemberMetadataTypeDescriptor, value: EditorValue): boolean => {
    switch (descriptor.valueKind) {
        case "number":
            return value !== null && value !== undefined && !Number.isNaN(value);
        case "enum":
            return typeof value === "string" && value.length > 0 &&
                !!descriptor.allowedValues?.includes(value);
        case "list":
            return Array.isArray(value) && value.length > 0;
        case "json": {
            const object = (value ?? {}) as Record<string, string>;
            const fields = descriptor.fields ?? [];
            const anyPresent = fields.some((f) => (object[f.name] ?? "").toString().trim() !== "");
            const numbersOk = fields
                .filter((f) => f.valueKind === "number")
                .every((f) => {
                    const raw = (object[f.name] ?? "").toString().trim();
                    return raw === "" || !Number.isNaN(Number(raw));
                });
            return anyPresent && numbersOk;
        }
        case "text":
        default:
            return typeof value === "string" && value.trim().length > 0;
    }
};

// Human-readable summary of a stored value for read-only rows.
export const summariseStoredValue = (
    descriptor: MemberMetadataTypeDescriptor,
    stored: string | null | undefined,
): string => {
    if (stored === undefined || stored === null || stored === "") return "Not set";
    if (descriptor.valueKind === "json") {
        try {
            const object = JSON.parse(stored) as Record<string, string>;
            const parts = Object.values(object).filter(Boolean);
            return parts.length ? parts.join(" · ") : "Set";
        } catch {
            return "Set";
        }
    }
    return stored;
};

interface FieldEditorProps {
    field: MemberMetadataField;
    value: string;
    onChange: (value: string) => void;
}

const JsonFieldEditor: React.FC<FieldEditorProps> = ({ field, value, onChange }) => {
    if (field.valueKind === "enum") {
        return (
            <Select
                value={value || undefined}
                onChange={onChange}
                options={(field.allowedValues ?? []).map((v) => ({ label: v, value: v }))}
                placeholder={field.label}
                style={{ width: "100%" }}
                data-testid={`metadata-json-field-${field.name}`}
            />
        );
    }
    return (
        <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.label}
            data-testid={`metadata-json-field-${field.name}`}
        />
    );
};

interface MetadataValueEditorProps {
    descriptor: MemberMetadataTypeDescriptor;
    value: EditorValue;
    onChange: (value: EditorValue) => void;
}

// Renders the value control matching the descriptor's value kind. Controlled:
// the parent owns the EditorValue.
export const MetadataValueEditor: React.FC<MetadataValueEditorProps> = ({ descriptor, value, onChange }) => {
    switch (descriptor.valueKind) {
        case "number":
            return (
                <InputNumber
                    inputMode="decimal"
                    value={value as number | null}
                    onChange={(v) => onChange(v)}
                    style={{ width: "100%" }}
                    placeholder={descriptor.label}
                    data-testid="metadata-value-number"
                />
            );
        case "enum":
            return (
                <Select
                    value={(value as string) || undefined}
                    onChange={(v) => onChange(v)}
                    options={(descriptor.allowedValues ?? []).map((v) => ({ label: v, value: v }))}
                    placeholder={`Select ${descriptor.label.toLowerCase()}`}
                    style={{ width: "100%" }}
                    data-testid="metadata-value-enum"
                />
            );
        case "list":
            return (
                <Select
                    mode={descriptor.allowedValues && descriptor.allowedValues.length ? "multiple" : "tags"}
                    value={(value as string[]) ?? []}
                    onChange={(v) => onChange(v)}
                    options={(descriptor.allowedValues ?? []).map((v) => ({ label: v, value: v }))}
                    placeholder={`Add ${descriptor.label.toLowerCase()}`}
                    style={{ width: "100%" }}
                    data-testid="metadata-value-list"
                />
            );
        case "json": {
            const object = (value as Record<string, string>) ?? {};
            return (
                <Space direction="vertical" style={{ width: "100%" }} size={tokens.space[2]} data-testid="metadata-value-json">
                    {(descriptor.fields ?? []).map((field) => (
                        <div key={field.name}>
                            <div style={{ fontSize: tokens.fontSize.xs, color: tokens.color.text.secondary, marginBottom: tokens.space[1] }}>
                                {field.label}
                            </div>
                            <JsonFieldEditor
                                field={field}
                                value={object[field.name] ?? ""}
                                onChange={(fieldValue) => onChange({ ...object, [field.name]: fieldValue })}
                            />
                        </div>
                    ))}
                </Space>
            );
        }
        case "text":
        default:
            return (
                <Input
                    value={value as string}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={descriptor.label}
                    data-testid="metadata-value-text"
                />
            );
    }
};
